import { type ReactElement, useId } from 'react'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import { useRequiredParams } from '@util'
import { isAdminRole, type RoleData, type RoleGetSuccess, type WithRoleId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { useForm } from '@mantine/form'
import { Stack, Switch } from '@mantine/core'
import { useToastError } from '@hooks'
import { useRemoveRole, useRole, useUpdateRole } from '@front/model'
import {
  AuditInfoSection,
  ButtonDelete,
  ButtonSave,
  DangerZone,
  EntityTabs,
  NotReady,
  Page,
  PermissionContent,
  PermissionTooltip,
  PrefetchedRoleInfo,
  type TSegmentedControlOption,
  TSection,
} from '@components'
import { RoleForm } from '../settings.role.add/RoleForm'
import { useConfirmMoveRoleToTrash } from './useConfirmMoveRoleToTrash'
import { RoleUsersTab } from './RoleUsersTab'

const ROLE_VIEW_TABS_VALUES = ['details', 'users', 'permissions'] as const
type RoleViewTab = (typeof ROLE_VIEW_TABS_VALUES)[number]

const ROLE_VIEW_TABS: TSegmentedControlOption<RoleViewTab>[] = [
  { label: 'Users', value: 'users', icon: 'group' },
  { label: 'Details', value: 'details', icon: 'info' },
  { label: 'Permissions', value: 'permissions', icon: 'shield' },
]

function EditRolePage(): ReactElement {
  const { roleId } = useRequiredParams<WithRoleId>()

  return <EditRole roleId={roleId} />
}

const EditRole = ({ roleId }: WithRoleId): ReactElement => {
  const { data, isLoading, error } = useRole({ roleId })
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringEnum<RoleViewTab>([...ROLE_VIEW_TABS_VALUES]).withDefault('details')
  )

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Role' isLoading={isLoading} />

  return <EditRoleForm activeTab={activeTab} roleData={data} setActiveTab={setActiveTab} />
}

type EditRoleFormProps = {
  roleData: RoleGetSuccess
  activeTab: RoleViewTab
  setActiveTab: (tab: RoleViewTab) => void
}

const EditRoleForm = ({ roleData, activeTab, setActiveTab }: EditRoleFormProps): ReactElement => {
  const { role } = roleData
  const detailsFormId = useId()

  const { update, isUpdating, error: updateError } = useUpdateRole()
  const { remove, isRemoving, error: removeError } = useRemoveRole()
  useToastError(updateError)
  useToastError(removeError)

  const form = useForm<RoleData>({
    initialValues: role,
  })
  const { getInputProps, values: formValues, onSubmit, setFieldValue, isDirty, resetDirty } = form

  const handleSaveSubmit = async (values: RoleData): Promise<void> => {
    await update(values)
    resetDirty(values)
    showNotification({ message: 'Saved Role Successfully' })
  }

  const handleMoveToTrash = async (): Promise<void> => {
    await remove({ roleId: role.roleId })

    showNotification({
      title: 'Role Moved to Trash',
      message: `You can restore ${role.name} anytime from the Trash.`,
      color: 'green',
    })
    setFieldValue('isDeleted', true)
    resetDirty()
  }
  const { confirmMoveToTrash, isChecking } = useConfirmMoveRoleToTrash({ role, moveToTrash: handleMoveToTrash })

  const isAdmin = isAdminRole(role)

  return (
    <Page>
      <Page.Heading
        metadata={{ title: 'Edit Role' }}
        title={<PrefetchedRoleInfo linked={false} role={role} size='md' />}
      >
        <ButtonSave disabled={!isDirty()} form={detailsFormId} loading={isUpdating} type='submit' />
      </Page.Heading>
      <Page.Content scrollArea>
        <Stack gap='xl'>
          <EntityTabs options={ROLE_VIEW_TABS} value={activeTab} onChange={setActiveTab} />
          <form className='w-full' id={detailsFormId} onSubmit={onSubmit(handleSaveSubmit)}>
            {activeTab === 'details' && (
              <Stack gap='xl'>
                <RoleForm form={form} />
                {role.isDeleted && (
                  <Switch
                    {...getInputProps('isDeleted', {
                      type: 'checkbox',
                    })}
                    color='red'
                    defaultChecked={formValues.isDeleted}
                    description='Switch off to restore this role from Trash'
                    label='In Trash'
                  />
                )}
                <TSection heading={{ title: 'Information' }}>
                  <AuditInfoSection {...role} />
                </TSection>
                {!role.isDeleted && (
                  <DangerZone>
                    <PermissionTooltip
                      label='You do not have permission to move a role to Trash'
                      permissionType='role:manage'
                      targetId={role.companyId}
                      extraConditions={[
                        {
                          active: isAdmin,
                          label: 'You cannot move the Admin role to Trash',
                        },
                      ]}
                    >
                      <ButtonDelete loading={isChecking || isRemoving} onClick={confirmMoveToTrash}>
                        Move Role to Trash
                      </ButtonDelete>
                    </PermissionTooltip>
                  </DangerZone>
                )}
              </Stack>
            )}
          </form>
          {activeTab === 'users' && <RoleUsersTab role={role} />}
          {activeTab === 'permissions' && <PermissionContent entityId={role.roleId} />}
        </Stack>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/settings/role/$roleId/')({
  component: EditRolePage,
})
